// messageFamily — collapse one error MESSAGE into the family of messages that
// are the same defect.
//
// Why: Sentry groups on the minified stack, so one defect mints a new issue on
// every deploy (F329 -76/-77/-78 in six hours) and one parametrised RAISE mints
// a new issue per parameter — "Invoice number AGF031 is already used by another
// invoice." and its nine siblings were SEVEN separate Sentry issues and ten
// "new groups" in the daily digest. The family is what a human reads as "that
// one again".
//
// This is the TS twin of the `family` expression in scripts/support-digest.sh.
// Both do the same four things in the same order (whitespace → UUIDs → quoted
// values → any token carrying a digit) so an issue title in Sentry and a group
// heading in the digest read the same. scripts/supportDigestFamily.test.mjs
// pins the SQL against these same fixtures.
//
// ⚠️ The normaliser is deliberately BLUNT: anything with a digit in it goes.
// Worker numbers, invoice numbers, pallet refs, tenant UUIDs, row counts and
// chunk hashes are all parameters, and none of them separate two defects. What
// keeps two genuinely different faults apart is the rest of the fingerprint —
// the SQLSTATE and the `fn_name:` prefix — not the message text.

const UUID_RE   = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi
// Vite chunk/asset filenames: `PalletDetail-BQ7fK2xZ.js`, `index-a1b2c3d4.css`.
// Replaced before the digit rule so the family still says a chunk was involved.
const CHUNK_RE  = /[A-Za-z0-9_$]+-[A-Za-z0-9_$]{6,}\.(?:js|mjs|css|map)/g
// A leading `fn_name: ` — how our own RPC RAISEs identify themselves
// (`attach_bins_to_consignment: bin … is already on another consignment`).
// Kept VERBATIM: two RPCs that refuse with the same wording must not collapse
// into one family. Requires an underscore so `TypeError:` / `Error:` are not
// mistaken for a function name.
const FN_PREFIX_RE = /^([a-z][a-z0-9]*(?:_[a-z0-9]+)+:\s+)/

const MAX_LEN = 140

/** Collapse a message to its family. Stable, side-effect free, no allocation traps. */
export function messageFamily(input: unknown): string {
  const raw = typeof input === 'string' ? input : String(input ?? '')
  // 1. Whitespace first — on its own this fixes the multi-line split that made
  //    one BCEA-cap message look like several groups.
  const flat = raw.replace(/\s+/g, ' ').trim()
  if (!flat) return ''

  const m = FN_PREFIX_RE.exec(flat)
  const prefix = m ? m[1].replace(/\s+$/, ' ') : ''
  const body = m ? flat.slice(m[1].length) : flat

  const normalised = body
    .replace(UUID_RE, '<uuid>')
    .replace(CHUNK_RE, '<chunk>')
    // 2. Quoted values — constraint names, codes, operator input. The quotes
    //    stay so the shape of the sentence survives.
    .replace(/"[^"]*"/g, '"<v>"')
    .replace(/'[^']*'/g, "'<v>'")
    // 3. Any whitespace-delimited token carrying a digit: AGF031, INV-2026-0007,
    //    2026/0012, F329, 14.726, 4c1e-… — all parameters.
    .replace(/\S*\d\S*/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim()

  return (prefix + normalised).slice(0, MAX_LEN)
}

// SQLSTATE-shaped code, as PostgREST hands it to us: five characters, digits
// and upper-case letters. Covers 23505, P0001, 22023, and our own custom codes
// (OPTLK, PRDLK). PostgREST's own codes (PGRST116) are longer, so they are
// matched separately.
const SQLSTATE_RE = /^(?:[0-9A-Z]{5}|PGRST\d{3})$/

/** The SQLSTATE / PostgREST code on a thrown error, when it carries one. */
export function dbErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null
  const code = (err as { code?: unknown }).code
  if (typeof code !== 'string') return null
  return SQLSTATE_RE.test(code) ? code : null
}

/**
 * The Sentry fingerprint for a PostgREST / RAISE-shaped error, or undefined
 * when the error is not database-shaped (those keep Sentry's own grouping —
 * a stack is a better discriminator than a sentence when there IS a stack).
 *
 * Database-shaped = it carries a SQLSTATE-ish `code`, OR the message opens
 * with one of our `fn_name:` RAISE prefixes.
 */
export function dbFingerprint(err: unknown, message: string): string[] | undefined {
  const code = dbErrorCode(err)
  const flat = message.replace(/\s+/g, ' ').trim()
  if (!code && !FN_PREFIX_RE.test(flat)) return undefined
  return ['db', code ?? '', messageFamily(flat)]
}
